import { OrthographicCamera } from '@react-three/drei';
import { ExperiencesThreeAnimationBridge, hookOnProgress } from './bridge';
import { useFlagModel } from 'app/hooks/useFlagModel';
import { useLayoutEffect, useRef } from 'react';
import { Rotator } from 'scripts/animations/rotator';
import { Disposer } from '@zajno/common/functions/disposer';
import { runEvent } from 'scripts/animations/three/utils/run';
import { HomeExperiences } from 'scripts/animations/three/home-experiences';
import { MusicPromotions } from './icons/1_music';
import { CuratorNetwork } from './icons/2_curator';
import { Quality } from './icons/3_quality';
import { Optimize } from './icons/4_optimize';
import { Support } from './icons/5_support';
import { Dashboard } from './icons/6_dashboard';
import { BackgroundColor } from './icons/common/materials';
import { AmbientLight, PrimaryLight, SecondaryLight, SecondaryLight2 } from './icons/common/lights';
import { useThree } from '@react-three/fiber';
import { DirectionalLightEcho, HemisphereLightEcho } from 'scripts/animations/echos';
import { BreakpointType, Breakpoints } from 'common/views/appBreakpoints';
import { PreCompiler } from 'scripts/animations/preCompiler';
import { createManualPromise } from '@zajno/common/async/misc';
import { LazyQueue } from 'scripts/components/lazy/lazyLoadComponent';

type Props = JSX.IntrinsicElements['group'] & {
    bgColor?: boolean;
};

const ScaleMap = {
    [BreakpointType.Desktop]: 200,
    [BreakpointType.Tablet]: 200,
    [BreakpointType.Mobile]: 150,
};

export const LazyPriority = 8;
const PrecompilePromise = createManualPromise();

LazyQueue.enqueue(() => PrecompilePromise.promise, LazyPriority);

export const Scene = (allProps: Props) => {

    const isActive = useFlagModel(ExperiencesThreeAnimationBridge.isActive);

    const { bgColor, ...props } = allProps;
    const mainVisible = props.visible ?? isActive;

    const rootRef = useRef<THREE.Group>();
    const rotateTargetRef = useRef<THREE.Group>();
    const scene = useThree(state => state.scene);

    useLayoutEffect(() => {
        scene.background = bgColor ? BackgroundColor : null;
    }, [scene, bgColor]);

    useLayoutEffect(() => {
        if (!mainVisible) {
            return;
        }

        const disposer = new Disposer();

        const idle = runEvent(HomeExperiences.Events.Idle, { root: rootRef.current });
        const scroll = runEvent(HomeExperiences.Events.Scroll, { root: rootRef.current });

        [idle, scroll].filter(Boolean).forEach(tl => disposer.add(() => tl.kill()));

        scroll.paused(true);
        disposer.add(
            hookOnProgress(scroll, ExperiencesThreeAnimationBridge.progress),
        );
        return () => disposer.dispose();
    }, [mainVisible]);

    return (
        <group
            ref={rootRef}
            {...props}
            visible={mainVisible}
        >
            <PreCompiler
                name='ExperienceThreeAnimation'
                rootRef={rootRef}
                manualPromise={PrecompilePromise}
                withLazyPriority={LazyPriority - 1}
            />

            {mainVisible && <Rotator target={rotateTargetRef} />}

            <DirectionalLightEcho source={PrimaryLight} />
            <DirectionalLightEcho source={SecondaryLight} />
            <DirectionalLightEcho source={SecondaryLight2} />
            <HemisphereLightEcho source={AmbientLight} />

            <OrthographicCamera
                name="Camera"
                makeDefault={true}
                zoom={5}
                far={100000}
                near={-100000}
                position={[0, 0.91, 1000]}
                rotation={[0, 0, 0]}
            />
            <group
                name="All_Icons_static"
                scale={ScaleMap[Breakpoints.Current.breakpoint.name]}
                ref={rotateTargetRef}
            >
                <group name="Scene_0" rotation={[-Math.PI, 0, -Math.PI]}>
                    <MusicPromotions />
                    <CuratorNetwork />
                    <Quality useEnvMap={Breakpoints.isDesktop} />
                    <Optimize />
                    <Support />
                    <Dashboard />
                </group>
            </group>
        </group>
    );
};
